import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { CareEnvironment, CarePhase, carePhaseDefList, carePhaseDefs } from '@shared';
import {
  selectClient,
  selectClientUpdatesStatus,
  putClientCustomInferencesForClientByClientIdRequest,
  putClientCarePhaseDurationSelectionForClientByClientIdRequest,
  putClientCalculationSettingsByClientIdRequest,
  putCareEnvironmentSelectionsForClientByClientIdRequest,
  putClientCareEnvironmentCostsForClientByClientIdRequest,
  resetClientUpdatesStatus,
} from '@client/model';
import { useDebounce } from '@client/hooks/useDebounce';
import type { EditAssumptionsFormFields } from './use-edit-assumptions-form';

interface AutoSaveValues {
  formValues: EditAssumptionsFormFields;
  selections: Partial<Record<CarePhase, CareEnvironment | null>>;
  costs: CareEnvironmentCosts;
  clientHasStartedLtc: boolean;
}

const STATUS_KEYS: ClientUpdateKeys[] = [
  'ltcAtAge',
  'phaseDurations',
  'healthCareInflationRate',
  'careEnvironmentSelections',
  'careEnvironmentCosts',
];

export function useAutoSaveAssumptions({ formValues, selections, costs, clientHasStartedLtc }: AutoSaveValues) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const clientStatus = useSelector(selectClientUpdatesStatus);

  const hasHydrated = useRef(false);
  const lastSavedLtcAtAge = useRef(formValues.ltcAtAge);
  const lastSavedInflation = useRef(formValues.healthcareInflation);
  const lastSavedDurations = useRef(serializeDurations(formValues));
  const lastSavedSelections = useRef(JSON.stringify(selections));
  const lastSavedCosts = useRef(JSON.stringify(costs));

  const debouncedLtcAtAge = useDebounce(formValues.ltcAtAge, 500);
  const debouncedInflation = useDebounce(formValues.healthcareInflation, 500);
  const debouncedDurations = useDebounce(serializeDurations(formValues), 500);

  // Skip auto-saving on initial mount
  useEffect(() => {
    hasHydrated.current = true;
  }, []);

  // Auto-save ltcAtAge
  useEffect(() => {
    if (!hasHydrated.current || clientHasStartedLtc) return;
    if (debouncedLtcAtAge === lastSavedLtcAtAge.current) return;
    lastSavedLtcAtAge.current = debouncedLtcAtAge;

    dispatch(
      putClientCustomInferencesForClientByClientIdRequest({
        clientId: client.clientId,
        clientCustomInferences: { ltcAtAge: debouncedLtcAtAge },
      }),
    );
  }, [debouncedLtcAtAge, clientHasStartedLtc, client.clientId, dispatch]);

  // Auto-save phase durations
  useEffect(() => {
    if (!hasHydrated.current) return;
    if (debouncedDurations === lastSavedDurations.current) return;
    lastSavedDurations.current = debouncedDurations;

    dispatch(
      putClientCarePhaseDurationSelectionForClientByClientIdRequest({
        clientId: client.clientId,
        carePhaseDurationSelections: parseDurations(debouncedDurations),
      }),
    );
  }, [debouncedDurations, client.clientId, dispatch]);

  // Auto-save healthcare inflation
  useEffect(() => {
    if (!hasHydrated.current) return;
    if (debouncedInflation === lastSavedInflation.current) return;
    lastSavedInflation.current = debouncedInflation;

    dispatch(
      putClientCalculationSettingsByClientIdRequest({
        clientId: client.clientId,
        clientCalculationSettings: {
          healthCareInflationRate: debouncedInflation / 100,
        },
      }),
    );
  }, [debouncedInflation, client.clientId, dispatch]);

  // Auto-save selections (immediate, no debounce)
  useEffect(() => {
    if (!hasHydrated.current) return;
    const serialized = JSON.stringify(selections);
    if (serialized === lastSavedSelections.current) return;
    lastSavedSelections.current = serialized;

    dispatch(
      putCareEnvironmentSelectionsForClientByClientIdRequest({
        clientId: client.clientId,
        careEnvironmentSelections: selections,
      }),
    );
  }, [selections, client.clientId, dispatch]);

  // Auto-save costs (immediate, no debounce)
  useEffect(() => {
    if (!hasHydrated.current) return;
    const serialized = JSON.stringify(costs);
    if (serialized === lastSavedCosts.current) return;
    lastSavedCosts.current = serialized;

    dispatch(
      putClientCareEnvironmentCostsForClientByClientIdRequest({
        clientId: client.clientId,
        careEnvironmentCosts: costs,
      }),
    );
  }, [costs, client.clientId, dispatch]);

  // Monitor for errors and reset statuses
  useEffect(() => {
    for (const key of STATUS_KEYS) {
      const status = clientStatus[key];
      if (status === 'error') {
        toast.error('Failed to save changes', {
          description: 'Your changes could not be saved. They will be retried on your next edit.',
        });
        dispatch(resetClientUpdatesStatus(key));
      } else if (status === 'complete') {
        dispatch(resetClientUpdatesStatus(key));
      }
    }
  }, [clientStatus, dispatch]);

  const isSaving = STATUS_KEYS.some(key => clientStatus[key] === 'loading');

  const rawValuesRef = useRef({ formValues, clientHasStartedLtc });
  rawValuesRef.current = { formValues, clientHasStartedLtc };

  const flushPendingSaves = useCallback(() => {
    const { formValues: raw, clientHasStartedLtc: hasStarted } = rawValuesRef.current;

    if (!hasStarted && raw.ltcAtAge !== lastSavedLtcAtAge.current) {
      lastSavedLtcAtAge.current = raw.ltcAtAge;
      dispatch(
        putClientCustomInferencesForClientByClientIdRequest({
          clientId: client.clientId,
          clientCustomInferences: { ltcAtAge: raw.ltcAtAge },
        }),
      );
    }

    const currentDurations = serializeDurations(raw);
    if (currentDurations !== lastSavedDurations.current) {
      lastSavedDurations.current = currentDurations;
      dispatch(
        putClientCarePhaseDurationSelectionForClientByClientIdRequest({
          clientId: client.clientId,
          carePhaseDurationSelections: parseDurations(currentDurations),
        }),
      );
    }

    if (raw.healthcareInflation !== lastSavedInflation.current) {
      lastSavedInflation.current = raw.healthcareInflation;
      dispatch(
        putClientCalculationSettingsByClientIdRequest({
          clientId: client.clientId,
          clientCalculationSettings: {
            healthCareInflationRate: raw.healthcareInflation / 100,
          },
        }),
      );
    }
  }, [dispatch, client.clientId]);

  return { isSaving, flushPendingSaves };
}

function serializeDurations(formValues: EditAssumptionsFormFields): string {
  return JSON.stringify(
    Object.fromEntries(
      carePhaseDefList.map(carePhaseDef => [
        carePhaseDef.value,
        formValues[carePhaseDefs[carePhaseDef.value].key as keyof EditAssumptionsFormFields],
      ]),
    ),
  );
}

function parseDurations(serialized: string): Partial<Record<CarePhase, number>> {
  return JSON.parse(serialized);
}
